import { Temporal } from '@js-temporal/polyfill';

export const generateFileName = (string) =>
    string?.replace(/[^a-z0-9]/gi, '_').toLowerCase();

export const parseTemporal = (date) => {
    if (!date) return null;
    // timezone or offset present?
    const hasTZ = /[zZ]|[+-]\d{2}:\d{2}$/.test(date);

    if (hasTZ) {
        return Temporal.Instant.from(date).toZonedDateTimeISO('UTC');
    }

    // no timezone → keep it as-is
    return Temporal.PlainDateTime.from(date);
};

export const getDateTime = (date) => {
    if (!date) return null;
    const dt = parseTemporal(date);
    const d = dt.toPlainDate();
    return `${d.month}/${d.day}/${d.year}`;
};

export const getDateAndTime = (date) => {
    // kept for backwards compatibility with existing call sites
    if (!date) return null;
    const dt = parseTemporal(date);
    const d = dt.toPlainDate();
    return `${d.month}/${d.day}/${d.year}`;
};

export const getDateAndTimeStamp = (date) => {
    if (!date) return null;
    const dt = parseTemporal(date);
    const t = dt.toPlainTime();

    const hour12 = t.hour % 12 || 12;
    const ampm = t.hour < 12 ? 'AM' : 'PM';

    return `${hour12}:${String(t.minute).padStart(2, '0')} ${ampm}`;
};

export const capitalizeFirstLetter = (word) => word?.[0].toUpperCase() + word?.substring(1);

export const getUserActionLabelForHeader = (action = '') => {
    if (!action) return undefined;
    if (action.toLowerCase() === 'approve') return 'Approved';
    if (action.toLowerCase() === 'deny') return 'Denied';
    if (action.toLowerCase() === 'approveonce') return 'Approved Once';
    return action;
};

export const getLastAlertStage = (alert) => {
    const stages = ['entered', 'initReview', 'labelPrintedOn', 'packed', 'checked', 'placedInTote'];

    let lastStage = null;
    for (let i = 0; i < stages.length; i++) {
        const stage = stages[i];
        if (alert?.[stage] !== null) {
            lastStage = stage;
        } else {
            break;
        }
    }

    if (lastStage === null) return null;

    switch (lastStage) {
        case 'entered':
            return 'Entered';
        case 'initReview':
            return 'Initial Review';
        case 'labelPrintedOn':
            return 'Label Printed On';
        case 'packed':
            return 'Packed';
        case 'checked':
            return 'Checked';
        case 'placedInTote':
            return 'Placed In Tote';
        default:
            return null;
    }
};

export const checkAlertStatus = (alert) => {
    if (!alert) return null;
    if (alert.posted || alert.deleted || alert.profileOnly === true) {
        if (alert.posted) return 'POSTED';
        if (alert.deleted) return 'DELETED';
        if (alert.profileOnly) return 'PROFILED';
    }
    return null;
};

export const checkQuantity = (alert) => {
    if (!alert?.archiveDtTm) return false;
    return alert.qty !== alert.orgQty;
};

export const checkSupply = (alert) => {
    if (!alert?.archiveDtTm) return false;
    return alert.daysSupply !== alert.orgDaysSupply;
};

/**
 * Alerts may send comments in `issues`, or widget-specific lists while `issues` is null/empty:
 * `nonCoveredCommentList`, `openBatchCommentList` (same item shape: comment, loggedDateTime, qetDdescription, etc.).
 * `priorAuthComments` is a single plain string rather than a list.
 * Returns: { loggedAt, comment, reason? } for UI and print.
 */
const mapCommentListItem = (item) => ({
    loggedAt: item.loggedAt ?? item.loggedDateTime,
    comment: item.comment,
    reason:
        item.reason ??
        item.qetDdescription ??
        (typeof item.issues === 'string' && item.issues.trim() ? item.issues : undefined),
});

const filterAndMapCommentList = (arr) =>
    (Array.isArray(arr) ? arr : [])
        .filter((item) => item && item.comment != null && String(item.comment).trim() !== '')
        .map(mapCommentListItem);

export const getAlertCommentsForDisplay = (alert) => {
    if (!alert || typeof alert !== 'object') return [];

    const fromIssues = filterAndMapCommentList(alert.issues);
    if (fromIssues.length > 0) return fromIssues;

    const ncl = alert.nonCoveredCommentList ?? alert.NonCoveredCommentList;
    const fromNc = filterAndMapCommentList(ncl);
    if (fromNc.length > 0) return fromNc;

    const obl = alert.openBatchCommentList ?? alert.OpenBatchCommentList;
    const fromObl = filterAndMapCommentList(obl);
    if (fromObl.length > 0) return fromObl;

    const pac = alert.priorAuthComments ?? alert.PriorAuthComments;
    if (typeof pac === 'string' && pac.trim() !== '') {
        return [{ loggedAt: alert.alertUpdatedOn ?? alert.alertCreatedOn, comment: pac }];
    }
    return [];
};